import { msg } from "@lingui/core/macro";
import type { MessageDescriptor } from "@lingui/core";
import { useLingui } from "@lingui/react";

import { useAssetLookup } from "../../api/useAssetLookup";
import { formatGameTime } from "../../format/formatGameTime";
import { BUILD_KIND_TITLE } from "../../model/BUILD_KIND_TITLE";
import type { FindingCategory, FindingSeverity } from "../../model/Finding";
import { findingsOf } from "../../model/findingsOf";
import { hasImpact } from "../../model/hasImpact";
import { lateDelayRange } from "../../model/lateDelayRange";
import type { MatchMetric } from "../../model/MatchMetric";
import { metricComparison } from "../../model/metricComparison";
import { METRIC_LABELS } from "../../model/METRIC_LABELS";
import type { Report } from "../../model/Report";
import { sectionSeverity } from "../../model/sectionSeverity";

const NO_ISSUES = msg({ id: "analysis.traffic_light.no_issues", message: "No issues found" });

const BUY_LATE = msg({
  id: "analysis.overview.buy.late",
  message: "{count, plural, one {# key item late} other {# key items late}}",
});
const BUY_DELAY_RANGE = msg({
  id: "analysis.overview.buy.delay_range",
  message: "{from}–{to} min later than usual",
});
const BUY_DELAY_ONE = msg({
  id: "analysis.overview.buy.delay_one",
  message: "{minutes} min later than usual",
});
const BUY_LIKE_STRONG = msg({
  id: "analysis.overview.buy.key_like_strong",
  message: "key items on time",
});
const BUY_TOTAL = msg({
  id: "analysis.overview.buy.total",
  message: "{count, plural, one {# item} other {# items}}",
});
const BUY_RARE = msg({
  id: "analysis.overview.buy.rare",
  message: "{count, plural, one {# rare pick} other {# rare picks}}",
});
const BUY_BUILD_FINE = msg({
  id: "analysis.overview.buy.build_fine",
  message: "{build} — everything on time for it",
});
const BUY_BUILD_UNUSUAL = msg({
  id: "analysis.overview.buy.build_unusual",
  message: "Strong {hero} players almost never build like this",
});
const BUY_DEFENSE_LATE = msg({
  id: "analysis.overview.buy.defense_late",
  message:
    "{kind, select, bullet {bullet} spirit {spirit} other {melee}} resistance {minutes, plural, one {# minute} other {# minutes}} late",
});
const BUY_DEFENSE_MISSING = msg({
  id: "analysis.overview.buy.defense_missing",
  message: "no {kind, select, bullet {bullet} spirit {spirit} other {melee}} resistance in time",
});

const DEATHS_TOTAL = msg({
  id: "analysis.overview.deaths.total",
  message: "{count, plural, one {# death} other {# deaths}}",
});
const DEATHS_TOP_KILLER = msg({
  id: "analysis.overview.deaths.top_killer",
  message: "{hero} killed you {count, plural, one {# time} other {# times}}",
});
const DEATHS_BAD_FIGHTS = msg({
  id: "analysis.overview.deaths.bad_fights",
  message: "in {count} there was time to retreat",
});
const DEATHS_NONE = msg({ id: "analysis.overview.deaths.none", message: "You never died" });
const DEATHS_NO_REPEAT = msg({
  id: "analysis.overview.deaths.different_killers",
  message: "different heroes killed you",
});

const FIGHTS_SUMMARY = msg({
  id: "analysis.overview.fights.summary",
  message:
    "{count, plural, one {An enemy escaped you with low HP once} other {Enemies escaped you with low HP # times}}",
});
const FIGHTS_AFTER_LANE = msg({
  id: "analysis.overview.fights.after_lane",
  message:
    "{count, plural, one {After the lane an enemy escaped you with low HP once} other {After the lane enemies escaped you with low HP # times}}",
});
const FIGHTS_MISSED = msg({
  id: "analysis.overview.fights.missed",
  message: "{count, plural, one {# fight without you} other {# fights without you}}",
});
const FIGHTS_SUMMARY_NONE = msg({
  id: "analysis.overview.fights.summary_none",
  message: "No enemy escaped you with low HP",
});

const PROBLEMS = msg({
  id: "analysis.overview.problems",
  message: "{count, plural, one {# problem} other {# problems}}",
});
const STRENGTHS = msg({
  id: "analysis.overview.strengths",
  message: "{count, plural, one {# strength} other {# strengths}}",
});
const LANE = msg({
  id: "analysis.overview.lane_outcome",
  message: "{outcome, select, won {Lane won} lost {Lane lost} other {Even lane}}",
});
const LANE_SOULS = msg({
  id: "analysis.overview.lane_souls",
  message: "{diff} souls vs lane opponents by {time}",
});
const METRIC_COMPARED = msg({
  id: "analysis.overview.metric_compared",
  message: "{metric}: {comparison}",
});

// Итог категории для заголовка секции: оценка (цвет) и одна строка под названием.
export type CategoryOverview = {
  severity: FindingSeverity;
  summary: string;
};

// Итоги категорий одного разбора. Тексты собираются из параметров находок и подробностей:
// бэкенд присылает коды и числа, слова — здесь. Оценка — по важным находкам (`sectionSeverity`).
export const useCategoryOverview = (
  report: Report,
): ((category: FindingCategory) => CategoryOverview) => {
  const { i18n } = useLingui();
  const findHero = useAssetLookup("heroes");
  const t = (descriptor: MessageDescriptor, values?: Record<string, unknown>): string =>
    i18n._({ ...descriptor, values });
  const { findings, details, match } = report;
  const rated = hasImpact(findings);
  const inCategory = (category: FindingCategory) =>
    findings.filter((finding) => finding.category === category);
  // Без своей находки: «3 проблемы», «2 сильные стороны» или «ошибок не нашли».
  const generic = (category: FindingCategory): string => {
    const all = inCategory(category);
    const bad = all.filter((finding) => finding.severity !== "good").length;
    return bad > 0
      ? t(PROBLEMS, { count: bad })
      : all.length > 0
        ? t(STRENGTHS, { count: all.length })
        : t(NO_ISSUES);
  };
  // Метрика словами: «Души в минуту: как у сильных игроков».
  const metricLine = (metric: MatchMetric): string | null => {
    const fromDetails = details?.metrics.find((m) => m.metric === metric && m.category !== null);
    const fromFinding = findingsOf(findings, "metric").find((f) => f.params.metric === metric);
    const severity = fromDetails?.severity ?? fromFinding?.severity ?? null;
    const percentile = fromDetails?.percentile ?? fromFinding?.params.percentile;
    if (percentile === undefined) {
      return null;
    }
    const comparison = metricComparison(severity, percentile);
    return t(METRIC_COMPARED, {
      metric: i18n._(METRIC_LABELS[metric]),
      comparison: i18n._({ ...comparison.descriptor, values: comparison.values }),
    });
  };

  const buy = (): string => {
    const build = details?.build;
    if (build?.outcome === "unusual") {
      return t(BUY_BUILD_UNUSUAL, { hero: findHero(match.hero_id).name });
    }
    const late = findingsOf(findings, "item_bought_late");
    const delay = lateDelayRange(findings);
    const [defense] = findingsOf(findings, "defense_mismatch");
    const parts: string[] = [];
    if (late.length > 0) {
      parts.push(t(BUY_LATE, { count: late.length }));
    }
    if (defense !== undefined) {
      const { bought_at_s: bought, baseline_at_s: baseline, damage_kind: kind } = defense.params;
      parts.push(
        bought != null && baseline != null && bought > baseline
          ? t(BUY_DEFENSE_LATE, {
              kind,
              minutes: Math.max(Math.round((bought - baseline) / 60), 1),
            })
          : t(BUY_DEFENSE_MISSING, { kind }),
      );
    }
    // Своя сборка сильных: итог — по ней; редкие у всех предметы для неё обычны.
    if (build?.outcome === "alternative") {
      const name = t(BUILD_KIND_TITLE, { kind: build.kind });
      return parts.length > 0 ? `${name}: ${parts.join(", ")}` : t(BUY_BUILD_FINE, { build: name });
    }
    const rare = details?.purchases.filter((p) => p.verdict === "rare").length ?? 0;
    if (rare > 0) {
      parts.push(t(BUY_RARE, { count: rare }));
    }
    if (details === null) {
      if (delay !== null) {
        return delay.from === delay.to
          ? t(BUY_DELAY_ONE, { minutes: delay.from })
          : t(BUY_DELAY_RANGE, delay);
      }
      return parts[0] ?? generic("buy");
    }
    const total = t(BUY_TOTAL, { count: details.purchases.length });
    if (parts.length > 0) {
      return `${total}: ${parts.join(", ")}`;
    }
    return details.purchases.some((p) => p.verdict === "on_time")
      ? `${total}: ${t(BUY_LIKE_STRONG)}`
      : total;
  };

  const deaths = (): string => {
    if (match.deaths === 0) {
      return t(DEATHS_NONE);
    }
    const [top] = findingsOf(findings, "killed_by_hero");
    const badFights = details?.deaths.filter((death) => death.fight !== null).length ?? 0;
    const parts = [
      ...(badFights > 0 ? [t(DEATHS_BAD_FIGHTS, { count: badFights })] : []),
      top !== undefined
        ? t(DEATHS_TOP_KILLER, {
            hero: findHero(top.params.hero_id).name,
            count: top.params.deaths,
          })
        : t(DEATHS_NO_REPEAT),
    ];
    return `${t(DEATHS_TOTAL, { count: match.deaths })}: ${parts.join(", ")}`;
  };

  const fights = (): string => {
    const escaped = findingsOf(findings, "enemy_escaped").reduce(
      (sum, f) => sum + f.params.count,
      0,
    );
    const missed = findingsOf(findings, "missed_teamfight")[0]?.params.fights.length ?? 0;
    const parts = [
      ...(escaped > 0 ? [t(rated ? FIGHTS_AFTER_LANE : FIGHTS_SUMMARY, { count: escaped })] : []),
      ...(missed > 0 ? [t(FIGHTS_MISSED, { count: missed })] : []),
    ];
    if (parts.length > 0) {
      return parts.join(", ");
    }
    return metricLine("hero_damage_per_min") ?? t(FIGHTS_SUMMARY_NONE);
  };

  // «Фарм и линия»: итог линии, потом души в минуту против сильных игроков.
  const farm = (): string => {
    const [lane] = findingsOf(findings, "lane_result");
    const souls = metricLine("souls_per_min");
    if (lane !== undefined || details?.lane != null) {
      const outcome = lane === undefined ? "even" : lane.severity === "good" ? "won" : "lost";
      const verdict = t(LANE, { outcome });
      if (souls !== null) {
        return `${verdict}. ${souls}`;
      }
      if (lane === undefined) {
        return verdict;
      }
      const diff = new Intl.NumberFormat(i18n.locale, { signDisplay: "always" }).format(
        lane.params.souls - lane.params.lane_souls,
      );
      return `${verdict}. ${t(LANE_SOULS, { diff, time: formatGameTime(lane.params.time_s) })}`;
    }
    return souls ?? generic("farm");
  };

  // «Прокачка»: копились ли очки способностей.
  const skills = (): string => metricLine("unspent_ap") ?? generic("skills");

  const byCategory: Record<FindingCategory, () => string> = {
    buy,
    farm,
    deaths,
    fights,
    skills,
  };

  // «Сильные так не собирают» — не находка, но и не «всё хорошо»: закуп не зелёный.
  const severity = (category: FindingCategory): FindingSeverity => {
    const computed = sectionSeverity(findings, category);
    return category === "buy" && details?.build?.outcome === "unusual" && computed === "good"
      ? "warn"
      : computed;
  };

  return (category) => ({
    severity: severity(category),
    summary: byCategory[category](),
  });
};
